import { useState, useEffect, useRef, useCallback } from 'react';
import { createSession, getSession, updateSession, getEnvironment, getSkippedSteps, listSessions } from '../services/api/provisioningApi';

const TOTAL_STEPS = 9;

const STEP_NAMES = [
  'Customer Creation',
  'Ruleset Configuration',
  'Metadata Fields',
  'Delivery Configuration',
  'Pipeline Information',
  'XML Schema',
  'Job Creation',
  'Validation',
  'Enable Customer'
];

/**
 * Custom hook for managing the provisioning wizard state and navigation.
 * Handles session persistence, step navigation, resume flow, and step skipping.
 */
export const useProvisioningWizard = () => {
  const [sessionId, setSessionId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [customerId, setCustomerId] = useState(null);
  const [referenceCustomerId, setReferenceCustomerId] = useState(null);
  const [stepCompletion, setStepCompletion] = useState({});
  const [stepData, setStepData] = useState({});
  const [environment, setEnvironment] = useState(null);
  const [isProduction, setIsProduction] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasActiveSession, setHasActiveSession] = useState(false);
  const [flowType, setFlowType] = useState('new_customer');
  const [skippedSteps, setSkippedSteps] = useState([]);
  const hasFetched = useRef(false);

  /**
   * Fetch environment info on mount
   */
  const fetchEnvironment = useCallback(async () => {
    try {
      const response = await getEnvironment();
      setEnvironment(response.data.effectiveEnvironment);
      setIsProduction(response.data.isProduction);
    } catch (err) {
      setError('Failed to detect environment');
    }
  }, []);

  /**
   * Start a new wizard session
   */
  const startNewSession = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError(null);
      const response = await createSession(options);
      const session = response.data;
      setSessionId(session.id);
      setCurrentStep(session.currentStep || 1);
      setCustomerId(session.customerId || null);
      setReferenceCustomerId(null);
      setStepCompletion({});
      setFlowType(session.flowType || 'new_customer');
      setHasActiveSession(true);
      
      // Fetch skipped steps for existing customer flows
      if (session.flowType === 'existing_customer_new_ruleset') {
        try {
          const skippedResponse = await getSkippedSteps(session.id);
          setSkippedSteps(skippedResponse.data.skippedSteps || []);
        } catch {
          // Non-critical — proceed without skipping
        }
      }
      
      return session;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create session';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Resume an existing session by ID
   */
  const resumeSession = useCallback(async (id) => {
    try {
      setLoading(true);
      setError(null);
      const response = await getSession(id);
      const session = response.data;
      setSessionId(session.id);
      setCurrentStep(session.currentStep);
      setCustomerId(session.customerId);
      setReferenceCustomerId(session.referenceCustomerId);
      setHasActiveSession(true);

      // Parse step data if available
      if (session.stepDataJson) {
        try {
          const stepData = JSON.parse(session.stepDataJson);
          const completion = {};
          if (stepData.CustomerStep) completion[1] = true;
          if (stepData.RulesetStep) completion[2] = true;
          if (stepData.MetadataStep) completion[3] = true;
          if (stepData.DeliveryStep) completion[4] = true;
          if (stepData.PipelineStep) completion[5] = true;
          if (stepData.XmlSchemaStep) completion[6] = true;
          if (stepData.JobCreationStep) completion[7] = true;
          if (stepData.ValidationStep) completion[8] = true;
          setStepCompletion(completion);
        } catch {
          // Invalid JSON, start fresh completion tracking
        }
      }

      return session;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to resume session';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Check for existing in-progress sessions for the current user.
   * Returns the list of sessions (empty if none found).
   */
  const checkForExistingSessions = useCallback(async () => {
    try {
      const response = await listSessions('InProgress');
      return response.data || [];
    } catch {
      return [];
    }
  }, []);

  /**
   * Navigate to the next step (only if current step is complete)
   */
  const goToNextStep = useCallback(() => {
    if (currentStep < TOTAL_STEPS && (stepCompletion[currentStep] || skippedSteps.includes(currentStep))) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, stepCompletion, skippedSteps]);

  /**
   * Navigate to the previous step
   */
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  /**
   * Navigate to a specific step (only if all prior steps are complete)
   */
  const goToStep = useCallback((step) => {
    if (step < 1 || step > TOTAL_STEPS) return;
    // Can go back freely, but forward only if prior steps complete
    if (step <= currentStep) {
      setCurrentStep(step);
    } else {
      // Check all steps before target are complete or skipped
      for (let i = 1; i < step; i++) {
        if (!stepCompletion[i] && !skippedSteps.includes(i)) return;
      }
      setCurrentStep(step);
    }
  }, [currentStep, stepCompletion, skippedSteps]);

  /**
   * Mark a step as complete and persist to backend
   */
  const completeStep = useCallback(async (step, stepDataJson = null) => {
    setStepCompletion(prev => ({ ...prev, [step]: true }));

    // Store step data locally for downstream steps to access
    if (stepDataJson) {
      setStepData(prev => ({ ...prev, [step]: stepDataJson }));
    }

    // Persist session state
    if (sessionId) {
      try {
        await updateSession(sessionId, {
          currentStep: step + 1,
          stepDataJson,
          customerId,
          referenceCustomerId
        });
      } catch (err) {
        // Non-critical — session state is also tracked locally
        console.warn('Failed to persist session state:', err);
      }
    }
  }, [sessionId, customerId, referenceCustomerId]);

  /**
   * Mark downstream steps as incomplete when a dependency changes
   */
  const invalidateFromStep = useCallback((fromStep) => {
    setStepCompletion(prev => {
      const updated = { ...prev };
      for (let i = fromStep; i <= TOTAL_STEPS; i++) {
        delete updated[i];
      }
      return updated;
    });
  }, []);

  /**
   * Check if forward navigation is allowed
   */
  const canGoForward = currentStep < TOTAL_STEPS && (stepCompletion[currentStep] || skippedSteps.includes(currentStep));
  const canGoBack = currentStep > 1;
  const isStepComplete = (step) => !!stepCompletion[step];
  const isLastStep = currentStep === TOTAL_STEPS;

  /**
   * Initial load — fetch environment
   */
  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchEnvironment().finally(() => setLoading(false));
    }
  }, [fetchEnvironment]);

  return {
    // Session state
    sessionId,
    currentStep,
    customerId,
    setCustomerId,
    referenceCustomerId,
    setReferenceCustomerId,
    stepCompletion,
    environment,
    isProduction,
    loading,
    error,
    hasActiveSession,

    // Navigation
    goToNextStep,
    goToPreviousStep,
    goToStep,
    canGoForward,
    canGoBack,
    isLastStep,

    // Step management
    completeStep,
    invalidateFromStep,
    isStepComplete,

    // Session lifecycle
    startNewSession,
    resumeSession,
    checkForExistingSessions,

    // Flow type and step skipping
    flowType,
    skippedSteps,
    isStepSkipped: (step) => skippedSteps.includes(step),

    // Step data (for passing data between steps)
    stepData,
    getStepData: (step) => stepData[step] || null,

    // Constants
    totalSteps: TOTAL_STEPS,
    stepNames: STEP_NAMES,

    // Utilities
    clearError: () => setError(null)
  };
};
